import {
    Minus,
    Plus,
} from "lucide-preact";
import type {
    ComponentChildren,
} from "preact";
import {
    useEffect,
    useState,
} from "preact/hooks";
import {
    Button,
    Card,
    CardContent,
    Input,
    Select,
    TextArea,
} from "../mod.tsx";
import type {
    BooleanSettingConfig,
    NumericSettingConfig,
    SelectSettingConfig,
    SettingConfig,
    TextSettingConfig,
} from "./types.ts";

const isTextSetting = (config: SettingConfig): config is TextSettingConfig =>
    config.type === "text" || config.type === "textarea";

const isBooleanSetting = (
    config: SettingConfig,
): config is BooleanSettingConfig => config.type === "boolean";

const isSelectSetting = (
    config: SettingConfig,
): config is SelectSettingConfig => config.type === "select";

type TextSettingProps = {
    config: TextSettingConfig;
    currentValue: string;
    onSettingChange: (key: string, value: string) => Promise<void>;
};

const TextSetting = ({
    config,
    currentValue,
    onSettingChange,
}: TextSettingProps): ComponentChildren => {
    const [localValue, setLocalValue] = useState(currentValue);

    useEffect(() => {
        setLocalValue(currentValue);
    }, [currentValue]);

    const handleBlur = (): void => {
        if (localValue !== currentValue) {
            onSettingChange(config.key, localValue);
        }
    };

    const InputComponent = config.type === "textarea" ? TextArea : Input;

    return (
        <div className="space-y-1">
            <span className="text-xs text-text-secondary">{config.label}</span>
            <InputComponent
                className="w-full text-xs"
                value={localValue}
                placeholder={config.placeholder}
                onChange={(event) =>
                    setLocalValue((event.target as HTMLInputElement).value)}
                onBlur={handleBlur}
            />
        </div>
    );
};

type SelectSettingProps = {
    config: SelectSettingConfig;
    currentValue: string;
    onSettingChange: (key: string, value: string) => Promise<void>;
};

const SelectSetting = ({
    config,
    currentValue,
    onSettingChange,
}: SelectSettingProps): ComponentChildren => {
    return (
        <div className="space-y-1">
            <span className="text-xs text-text-secondary">{config.label}</span>
            <Select
                className="w-full text-xs"
                value={currentValue}
                onChange={(event) =>
                    onSettingChange(
                        config.key,
                        (event.target as HTMLSelectElement).value,
                    )}
            >
                {config.options.map((option) => (
                    <option key={option.value} value={option.value}>
                        {option.label}
                    </option>
                ))}
            </Select>
        </div>
    );
};

type DispatcherSettingsCardProps = {
    settings: Record<string, string>;
    settingConfigs: SettingConfig[];
    onSettingChange: (key: string, value: string) => Promise<void>;
    title?: string;
    vertical?: boolean;
};

type SettingGroup = {
    group: string | undefined;
    configs: SettingConfig[];
};

const groupSettings = (configs: SettingConfig[]): SettingGroup[] => {
    const groups: SettingGroup[] = [];
    const groupMap = new Map<string | undefined, SettingConfig[]>();

    for (const config of configs) {
        const existing = groupMap.get(config.group);
        if (existing) {
            existing.push(config);
        } else {
            const items: SettingConfig[] = [config];
            groupMap.set(config.group, items);
            groups.push({
                group: config.group,
                configs: items,
            });
        }
    }

    return groups;
};

const defaultFormatValue = (value: number): string => String(value);

export const DispatcherSettingsCard = ({
    settings,
    settingConfigs,
    onSettingChange,
    title = "Dispatcher",
    vertical = true,
}: DispatcherSettingsCardProps): ComponentChildren => {
    const handleChange = (
        config: NumericSettingConfig,
        delta: number,
    ): void => {
        const current = parseFloat(settings[config.key] ?? config.defaultValue);
        const decimalPlaces =
            (config.step.toString().split(".")[1] || "").length;
        const next = parseFloat(
            Math.max(config.min, Math.min(config.max, current + delta)).toFixed(
                decimalPlaces,
            ),
        );
        if (next === current) return;
        onSettingChange(config.key, String(next));
    };

    const renderSetting = (config: SettingConfig): ComponentChildren => {
        if (isTextSetting(config)) {
            return (
                <TextSetting
                    key={config.key}
                    config={config}
                    currentValue={settings[config.key] ?? config.defaultValue}
                    onSettingChange={onSettingChange}
                />
            );
        }

        if (isSelectSetting(config)) {
            return (
                <SelectSetting
                    key={config.key}
                    config={config}
                    currentValue={settings[config.key] ?? config.defaultValue}
                    onSettingChange={onSettingChange}
                />
            );
        }

        if (isBooleanSetting(config)) {
            const isEnabled =
                (settings[config.key] ?? config.defaultValue) === "true";
            return (
                <div
                    key={config.key}
                    className="flex items-center justify-between gap-2"
                >
                    <span className="text-xs text-text-secondary">
                        {config.label}
                    </span>
                    <button
                        type="button"
                        role="switch"
                        aria-checked={isEnabled}
                        onClick={() =>
                            onSettingChange(
                                config.key,
                                isEnabled ? "false" : "true",
                            )}
                        className={`relative inline-flex h-5 w-9 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2 ${
                            isEnabled ? "bg-accent" : "bg-bg-tertiary"
                        }`}
                    >
                        <span
                            className={`pointer-events-none inline-block h-4 w-4 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                                isEnabled ? "translate-x-4" : "translate-x-0"
                            }`}
                        />
                    </button>
                </div>
            );
        }

        const currentValue = parseFloat(
            settings[config.key] ?? config.defaultValue,
        );
        const formatter = config.formatValue ?? defaultFormatValue;

        return (
            <div
                key={config.key}
                className="flex items-center justify-between gap-2"
            >
                <span className="text-xs text-text-secondary">
                    {config.label}
                </span>
                <div className="flex items-center gap-1">
                    <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleChange(config, -config.step)}
                        disabled={currentValue <= config.min}
                    >
                        <Minus className="w-3 h-3" />
                    </Button>
                    <span className="text-sm font-mono w-12 text-center">
                        {formatter(currentValue)}
                    </span>
                    <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleChange(config, config.step)}
                        disabled={currentValue >= config.max}
                    >
                        <Plus className="w-3 h-3" />
                    </Button>
                </div>
            </div>
        );
    };

    if (vertical) {
        const grouped = groupSettings(settingConfigs);

        return (
            <Card>
                <CardContent className="space-y-2">
                    <h3 className="text-sm font-medium text-text-primary mb-1">
                        {title}
                    </h3>
                    {grouped.map((section) => (
                        <div key={section.group ?? "__default"}>
                            {section.group && (
                                <div className="text-[10px] text-text-secondary font-medium uppercase tracking-wider mt-3 mb-1">
                                    {section.group}
                                </div>
                            )}
                            {section.configs.map(renderSetting)}
                        </div>
                    ))}
                </CardContent>
            </Card>
        );
    }

    return (
        <Card>
            <CardContent className="flex items-center gap-6 py-3 flex-wrap">
                <span className="text-sm font-medium text-text-primary">
                    {title}
                </span>
                {settingConfigs.map((config) => {
                    if (
                        isTextSetting(config) || isBooleanSetting(config) ||
                        isSelectSetting(config)
                    ) {
                        return renderSetting(config);
                    }

                    const currentValue = parseFloat(
                        settings[config.key] ?? config.defaultValue,
                    );
                    const formatter = config.formatValue ?? defaultFormatValue;

                    return (
                        <div
                            key={config.key}
                            className="flex items-center gap-2"
                        >
                            <span className="text-xs text-text-secondary">
                                {config.label}
                            </span>
                            <Button
                                variant="ghost"
                                size="sm"
                                onClick={() =>
                                    handleChange(config, -config.step)}
                                disabled={currentValue <= config.min}
                            >
                                <Minus className="w-3 h-3" />
                            </Button>
                            <span className="text-sm font-mono w-12 text-center">
                                {formatter(currentValue)}
                            </span>
                            <Button
                                variant="ghost"
                                size="sm"
                                onClick={() =>
                                    handleChange(config, config.step)}
                                disabled={currentValue >= config.max}
                            >
                                <Plus className="w-3 h-3" />
                            </Button>
                        </div>
                    );
                })}
            </CardContent>
        </Card>
    );
};
